import type { Column } from "@tanstack/vue-table";
import { computed } from "vue";
import { $t } from "@/services/i18n";
import { cn } from "@/utils/cn";
import Dropdown from "../../../primitives/dropdown/Dropdown";
import { tableHeaderCellVariant } from "../../../primitives/table/variants";
import Icon from "../../icon/Icon";
import Tooltip from "../../tooltip/Tooltip";

interface SortedHeaderOptions {
  align?: "left" | "right" | "center";
  tooltip?: string;
}

export function SortedHeader<T extends any>(name: string, options?: SortedHeaderOptions) {
  return ({ column }: { column: Column<T, unknown> }) => {
    const icon = computed<string>(() => {
      const isSorted = column.getIsSorted();

      if (isSorted === "desc") return "lucide:arrow-down";
      if (isSorted === "asc") return "lucide:arrow-up";
      return "lucide:chevrons-up-down";
    });

    return (
      <button
        class={cn(
          tableHeaderCellVariant(),
          "flex h-auto items-center gap-2 whitespace-nowrap rounded-sm bg-transparent p-0 focus-visible-default hover:text-strong focus-visible:text-brand-10 focus-visible:outline-offset-4",
          column.getIsSorted() && "text-foreground"
        )}
        onClick={() => column.toggleSorting()}
      >
        <span>{name}</span>
        {!!options?.tooltip && (
          <Tooltip
            text={options?.tooltip}
            class="max-w-80 whitespace-pre-wrap font-normal normal-case tracking-normal"
          >
            <Icon icon="lucide:info" class="h-3 w-3" />
          </Tooltip>
        )}
        <Icon icon={icon.value} class="-mr-1 h-4 w-4" />
      </button>
    );
  };
}

export function SortedHeaderPop<T extends any>(name: string, options?: SortedHeaderOptions) {
  return ({ column }: { column: Column<T, unknown> }) => {
    const icon = computed<string>(() => {
      const isSorted = column.getIsSorted();

      if (isSorted === "desc") return "lucide:arrow-down";
      if (isSorted === "asc") return "lucide:arrow-up";
      return "lucide:chevrons-up-down";
    });

    const styles = computed(() => {
      const base = "flex items-center";

      if (options?.align === "left") return cn(base, "justify-start");
      if (options?.align === "right") return cn(base, "justify-end");
      if (options?.align === "center") return cn(base, "justify-center");
    });

    return (
      <Dropdown.Root>
        <div class={[styles.value]}>
          <Dropdown.Trigger asChild>
            <button
              class={cn(
                tableHeaderCellVariant(),
                "flex h-auto items-center gap-2 whitespace-nowrap rounded-sm bg-transparent p-0 focus-visible-default hover:text-strong focus-visible:text-brand-10 focus-visible:outline-offset-4",
                column.getIsSorted() && "text-foreground"
              )}
            >
              <span>{name}</span>
              {!!options?.tooltip && (
                <Tooltip text={options?.tooltip}>
                  <Icon icon="lucide:info" class="h-3 w-3" />
                </Tooltip>
              )}
              <Icon icon={icon.value} class="-mr-1 h-4 w-4" />
            </button>
          </Dropdown.Trigger>
        </div>
        <Dropdown.Content align="start" alignOffset={0} loop>
          <Dropdown.Group>
            {column.getIsSorted() !== "asc" && (
              <Dropdown.Item
                onSelect={() => {
                  column.toggleSorting(false);
                }}
              >
                <Icon icon="lucide:arrow-up" class="mr-2 h-4 w-4" />
                {$t("asc")}
              </Dropdown.Item>
            )}
            {column.getIsSorted() !== "desc" && (
              <Dropdown.Item
                onSelect={() => {
                  column.toggleSorting(true);
                }}
              >
                <Icon icon="lucide:arrow-down" class="mr-2 h-4 w-4" />
                {$t("desc")}
              </Dropdown.Item>
            )}

            {!!column.getIsSorted() && (
              <>
                <Dropdown.Separator />
                <Dropdown.Item
                  onSelect={() => {
                    column.clearSorting();
                  }}
                >
                  <Icon icon="lucide:chevrons-up-down" class="mr-2 h-4 w-4" />
                  {$t("remove")}
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Group>
        </Dropdown.Content>
      </Dropdown.Root>
    );
  };
}

interface CustomHeaderOptions {
  tooltip?: string;
  align?: "left" | "right" | "center";
}

export function CustomHeader(name: string, options?: CustomHeaderOptions) {
  return () => {
    return (
      <div
        style={{
          textAlign: options?.align ?? "left",
        }}
        class="flex gap-1"
      >
        <span>{name}</span>
        {options?.tooltip && (
          <Tooltip text={options.tooltip}>
            <Icon icon="lucide:info" class="h-4 w-4" />
          </Tooltip>
        )}
      </div>
    );
  };
}
